import { FIELD_TYPE_OPTIONS, OPTION_FIELD_TYPES, normalizeFields } from './kyc-shared.js';

export function toEditableField(field, index) {
  const source = field && typeof field === 'object' ? field : {};
  const fallbackType = FIELD_TYPE_OPTIONS.includes(source.type) ? source.type : 'text';
  const persistedFieldId = String(source.id || source.uid || source.fieldId || '').trim();
  const fieldId = persistedFieldId || `field-id-${index}-${Math.random().toString(36).slice(2, 8)}`;
  const normalizedOptions = normalizeFields(source.options)
    .map((item) => {
      if (item && typeof item === 'object') {
        return String(item.label || item.value || '').trim();
      }
      return String(item ?? '').trim();
    })
    .filter(Boolean);
  const normalizedDependencies = normalizeFields(source.dependencies)
    .map((item) => {
      const dependency = item && typeof item === 'object' ? item : {};
      const impacts = normalizeFields(dependency.impacts)
        .map((value) => String(value ?? '').trim())
        .filter(Boolean);

      return {
        id: String(dependency.id || dependency.uid || '').trim(),
        fieldId: String(dependency.fieldIdRelated || dependency.fieldId || '').trim(),
        impacts,
        checked: typeof dependency.checked === 'boolean' ? dependency.checked : null,
        source: { ...dependency },
      };
    })
    .filter((item) => item.fieldId);

  return {
    localId: `field-${index}-${Math.random().toString(36).slice(2, 8)}`,
    fieldId,
    name: String(source.name || '').trim(),
    type: fallbackType,
    required: typeof source.mandatory === 'boolean'
      ? source.mandatory
      : (typeof source.required === 'boolean' ? source.required : false),
    immutable: typeof source.immutable === 'boolean' ? source.immutable : false,
    visible: typeof source.visible === 'boolean' ? source.visible : true,
    regExp: source.regExp ?? source.regexp ?? null,
    options: normalizedOptions,
    newOption: '',
    dependencies: normalizedDependencies,
    source: { ...source },
  };
}

export function toApiField(field) {
  const name = String(field.name || '').trim();
  const type = FIELD_TYPE_OPTIONS.includes(field.type) ? field.type : 'text';
  const source = field.source && typeof field.source === 'object' ? { ...field.source } : {};
  const sourceId = String(source.id || source.uid || '').trim();
  const dependencies = normalizeFields(field.dependencies)
    .map((item) => {
      const dependency = item && typeof item === 'object' ? item : {};
      const impacts = normalizeFields(dependency.impacts)
        .map((value) => String(value ?? '').trim())
        .filter(Boolean);

      return {
        fieldIdRelated: String(dependency.fieldId || '').trim(),
        impacts,
        checked: typeof dependency.checked === 'boolean' ? dependency.checked : null,
      };
    })
    .filter((item) => item.fieldIdRelated);

  delete source.id;
  delete source.uid;
  delete source.fieldId;

  return {
    ...source,
    ...(sourceId && !sourceId.startsWith('field-id-') ? { id: sourceId } : {}),
    name,
    type,
    mandatory: typeof field.required === 'boolean'
      ? field.required
      : (typeof source.mandatory === 'boolean' ? source.mandatory : Boolean(source.required)),
    immutable: typeof field.immutable === 'boolean'
      ? field.immutable
      : Boolean(source.immutable),
    visible: typeof field.visible === 'boolean' ? field.visible : (typeof source.visible === 'boolean' ? source.visible : true),
    regExp: field.regExp ?? source.regExp ?? source.regexp ?? null,
    options: OPTION_FIELD_TYPES.has(type)
      ? (Array.isArray(field.options) ? field.options : [])
          .map((item) => String(item ?? '').trim())
          .filter(Boolean)
          .map((item) => ({ label: item, value: item }))
      : [],
    dependencies,
  };
}

export function valuesToText(values) {
  return normalizeFields(values)
    .map((item) => String(item ?? '').trim())
    .filter(Boolean)
    .join('\n');
}

export function textToValues(value) {
  return String(value || '')
    .split('\n')
    .map((item) => item.trim())
    .filter(Boolean);
}

function padDatePart(value) {
  return String(value).padStart(2, '0');
}

function isValidDateParts(year, month, day) {
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    return false;
  }

  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return false;
  }

  const parsed = new Date(year, month - 1, day);
  return (
    parsed.getFullYear() === year
    && parsed.getMonth() === month - 1
    && parsed.getDate() === day
  );
}

function formatIsoDate(year, month, day) {
  return `${String(year).padStart(4, '0')}-${padDatePart(month)}-${padDatePart(day)}`;
}

export function normalizeDateToIso(value, localeHint = '') {
  const raw = String(value ?? '').trim();
  if (!raw) {
    return '';
  }

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const year = Number(isoMatch[1]);
    const month = Number(isoMatch[2]);
    const day = Number(isoMatch[3]);
    if (isValidDateParts(year, month, day)) {
      return formatIsoDate(year, month, day);
    }
    return raw;
  }

  const separatorMatch = raw.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
  if (separatorMatch) {
    const first = Number(separatorMatch[1]);
    const second = Number(separatorMatch[2]);
    const year = Number(separatorMatch[3]);
    const preferMonthFirst = String(localeHint || '').toLowerCase().startsWith('en-us');

    const variants = preferMonthFirst
      ? [[year, first, second], [year, second, first]]
      : [[year, second, first], [year, first, second]];

    for (const [candidateYear, candidateMonth, candidateDay] of variants) {
      if (isValidDateParts(candidateYear, candidateMonth, candidateDay)) {
        return formatIsoDate(candidateYear, candidateMonth, candidateDay);
      }
    }

    return raw;
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return raw;
  }

  return formatIsoDate(parsed.getFullYear(), parsed.getMonth() + 1, parsed.getDate());
}

export function normalizeImpactsForSourceType(impacts, sourceType, localeHint = '') {
  const normalizedImpacts = normalizeFields(impacts)
    .map((item) => String(item ?? '').trim())
    .filter(Boolean);

  if (sourceType !== 'date') {
    return normalizedImpacts;
  }

  return normalizedImpacts.map((item) => normalizeDateToIso(item, localeHint));
}
